import { useEffect, useMemo, useRef } from 'react';
import { Drawer, ScrollArea, Stack, Text, Title } from '@mantine/core';

interface StoryReferenceDrawerProps {
  opened: boolean;
  onClose: () => void;
  storyTitle: string;
  passage: string;
  /** Full text of the current question; used to pick a passage sentence to highlight. */
  question?: string;
  /** Option strings for the current question; also used as match signal. */
  optionTexts?: string[];
}

// Common short words we don't want to score on. Not exhaustive — anything a
// primary-school passage might use as content stays in.
const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'if', 'of', 'to', 'in', 'on', 'at',
  'for', 'with', 'was', 'were', 'is', 'are', 'be', 'been', 'being', 'do',
  'does', 'did', 'have', 'has', 'had', 'this', 'that', 'these', 'those',
  'it', 'its', 'they', 'them', 'their', 'there', 'here', 'so', 'as', 'by',
  'from', 'not', 'no', 'yes', 'up', 'down', 'out', 'about', 'what', 'which',
  'who', 'whom', 'when', 'where', 'why', 'how', 'you', 'your', 'he', 'she',
  'his', 'her', 'him', 'me', 'my', 'we', 'us', 'our', 'i',
]);

const MIN_TOKEN_LEN = 4;

function tokens(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= MIN_TOKEN_LEN && !STOPWORDS.has(t));
}

function splitSentences(passage: string): { text: string; start: number; end: number }[] {
  // Deliberately loose — matches ., !, ? followed by whitespace/end.
  const out: { text: string; start: number; end: number }[] = [];
  const re = /[^.!?]+[.!?]+["')\]]*|\S+$/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(passage)) !== null) {
    const text = m[0].trim();
    if (text) {
      out.push({ text, start: m.index, end: m.index + m[0].length });
    }
  }
  return out;
}

/**
 * Given the current question, guess which sentence of the passage is most
 * likely the one that answers it. Uses non-stopword token overlap; ties go
 * to the earliest sentence. Returns the index into the sentences array, or
 * -1 if nothing scored above zero.
 */
function pickRelevantSentence(
  sentences: { text: string }[],
  question: string | undefined,
  optionTexts: string[] | undefined,
): number {
  if (!question) return -1;
  const questionTokens = tokens(question);
  const optionTokens = (optionTexts ?? []).flatMap(tokens);
  const signal = new Set([...questionTokens, ...optionTokens]);
  if (signal.size === 0) return -1;

  let bestIdx = -1;
  let bestScore = 0;
  for (let i = 0; i < sentences.length; i++) {
    const senTokens = new Set(tokens(sentences[i].text));
    let score = 0;
    for (const t of signal) if (senTokens.has(t)) score++;
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function StoryReferenceDrawer({
  opened,
  onClose,
  storyTitle,
  passage,
  question,
  optionTexts,
}: StoryReferenceDrawerProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLSpanElement>(null);

  const sentences = useMemo(() => splitSentences(passage), [passage]);
  const relevantIdx = useMemo(
    () => pickRelevantSentence(sentences, question, optionTexts),
    [sentences, question, optionTexts],
  );

  // Scroll to the highlighted sentence when the drawer opens (or the current
  // question changes while it's open). Timeout because Mantine's Drawer
  // animates in — measuring before the transition finishes gives the wrong
  // offset.
  useEffect(() => {
    if (!opened || relevantIdx < 0) return;
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 250);
    return () => clearTimeout(t);
  }, [opened, relevantIdx]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={<Title order={4}>{storyTitle}</Title>}
      overlayProps={{ backgroundOpacity: 0.35, blur: 2 }}
    >
      <ScrollArea h="calc(100vh - 120px)" viewportRef={scrollAreaRef}>
        <Stack gap="xs" pr="md" pb="xl">
          <Text size="sm" c="dimmed" fs="italic">
            {relevantIdx >= 0
              ? 'The highlighted sentence is probably where the answer is.'
              : 'Re-read the passage as needed. The quiz keeps your progress.'}
          </Text>
          <Text size="md" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {sentences.map((s, i) => {
              const isHighlight = i === relevantIdx;
              const trailing = i < sentences.length - 1 ? ' ' : '';
              if (isHighlight) {
                return (
                  <span
                    key={i}
                    ref={highlightRef}
                    style={{
                      backgroundColor: 'var(--mantine-color-yellow-1)',
                      padding: '2px 4px',
                      borderRadius: '3px',
                    }}
                  >
                    {s.text}
                    {trailing}
                  </span>
                );
              }
              return <span key={i}>{s.text}{trailing}</span>;
            })}
          </Text>
        </Stack>
      </ScrollArea>
    </Drawer>
  );
}
